import React, { useState, useEffect, useCallback } from "react";
import styles from "./FiscalDeviceHealthScreen.module.scss";
import classNames from "classnames";
import { useSelector } from "react-redux";
import moment from "moment-timezone";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Area,
  AreaChart,
  ReferenceLine,
} from "recharts";
import fiscalDeviceService from "../../../../apis/services/admin/fiscalDeviceService";

// Time range options
const TIME_RANGES = [
  { label: "1h", value: 60 * 60 * 1000 },
  { label: "6h", value: 6 * 60 * 60 * 1000 },
  { label: "24h", value: 24 * 60 * 60 * 1000 },
  { label: "7d", value: 7 * 24 * 60 * 60 * 1000 },
  { label: "30d", value: 30 * 24 * 60 * 60 * 1000 },
];

// Helper functions
const formatDuration = (ms) => {
  if (!ms) return "-";
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
};

const formatDateTime = (timestamp) => {
  if (!timestamp) return "-";
  return moment(timestamp).tz(moment.tz.guess()).format("DD.MM.YYYY HH:mm:ss");
};

const formatChartTime = (timestamp) => {
  return new Date(timestamp).toLocaleTimeString("sr-RS", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatChartDate = (timestamp) => {
  return new Date(timestamp).toLocaleDateString("sr-RS", {
    day: "2-digit",
    month: "2-digit",
  });
};

const formatChartDateTime = (timestamp) => {
  return new Date(timestamp).toLocaleString("sr-RS", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const translateErrorType = (type) => {
  const translations = {
    NONE: "OK",
    PCSC_SERVICE_ERROR: "PC/SC greška",
    CARD_COMMUNICATION_ERROR: "Komunikacija",
    CARD_NOT_TRANSACTED: "Zauzeta",
    CARD_INVALID_HANDLE: "Invalid handle",
    PKCS11_INIT_ERROR: "PKCS11 greška",
    PIN_ERROR: "PIN greška",
    CERTIFICATE_ERROR: "Sertifikat",
    PHYSICAL_CARD_ERROR: "Fizička greška",
    UNKNOWN_ERROR: "Nepoznato",
  };
  return translations[type || ""] || type || "-";
};

const translateRecoveryAction = (action) => {
  const translations = {
    NONE: "Bez akcije",
    PCSC_RESTART: "Restart PC/SC servisa",
    CARD_RECONNECT: "Ponovno povezivanje kartice",
    PROTOCOL_RESET: "Reset protokola",
    PKCS11_REINIT: "Reinicijalizacija PKCS11",
    PIN_REVERIFY: "Ponovna verifikacija PIN-a",
    FULL_RESET: "Potpuni reset",
    PHYSICAL_INTERVENTION_REQUIRED: "Potrebna fizička intervencija",
  };
  return translations[action || ""] || action || "-";
};

const failureLabels = {
  unknownController: "Nepoznat kontroler",
  unknownItem: "Nepoznat artikal",
  pinRequired: "Potreban PIN",
  pinVerification: "Greška PIN-a",
  cardNotPresent: "Kartica nije prisutna",
  sdcNotConfigured: "SDC nije konfigurisan",
  lpfrSubmit: "LPFR greška",
  vsdcSubmit: "VSDC greška",
  dbSave: "Greška baze",
  fscSave: "FSC greška",
  pdfGeneration: "PDF greška",
  printing: "Greška štampe",
};

const getOverallHealthStatus = (fd) => {
  // Note: API returns auditPackageStat (singular) and vendingMetric (singular)
  const { smartCardHealth, applicationHealth, auditPackageStat, vendingMetric } = fd || {};

  // Critical conditions
  if (smartCardHealth?.physicalInterventionRequired) {
    return { status: "critical", color: "red", message: "Potrebna fizička intervencija na smart kartici!" };
  }
  if (applicationHealth?.deadlockedThreadCount > 0) {
    return { status: "critical", color: "red", message: "Detektovan deadlock - potreban restart!" };
  }
  if (vendingMetric?.cardLocked) {
    return { status: "critical", color: "red", message: "Kartica je zaključana!" };
  }
  if (vendingMetric?.wrongPinEntered) {
    return { status: "critical", color: "red", message: "Unet pogrešan PIN!" };
  }

  // Warning conditions
  const heapUsage = parseFloat(applicationHealth?.heapUsagePercent);
  if (heapUsage > 90) {
    return { status: "warning", color: "orange", message: "Visoka potrošnja memorije" };
  }
  if (applicationHealth?.hasStuckTasks) {
    return { status: "warning", color: "orange", message: "Zaglavljeni scheduled taskovi" };
  }
  if (auditPackageStat?.orphanedCount > 0) {
    return { status: "warning", color: "orange", message: `${auditPackageStat.orphanedCount} orphan audit paketa` };
  }
  if (smartCardHealth?.consecutiveErrorCount > 5) {
    return { status: "warning", color: "orange", message: "Višestruke greške smart kartice" };
  }
  const successRate = parseFloat(vendingMetric?.successRate);
  if (successRate && successRate < 95) {
    return { status: "warning", color: "orange", message: "Nizak success rate vending faktura" };
  }

  // Check if we have any data
  if (!smartCardHealth && !applicationHealth && !auditPackageStat && !vendingMetric) {
    return { status: "unknown", color: "gray", message: "Nema podataka o zdravlju uređaja" };
  }

  return { status: "healthy", color: "green", message: "Uređaj radi ispravno" };
};

const FiscalDeviceHealthScreen = ({ id }) => {
  const fiscalDevice = useSelector((state) => state.fiscalDevices.singleFiscalDevice);
  const data = fiscalDevice?.data;

  // Metrics history state
  const [timeRange, setTimeRange] = useState(TIME_RANGES[2]); // Default 24h
  const [metricsHistory, setMetricsHistory] = useState([]);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState(null);

  const healthStatus = getOverallHealthStatus(data);
  const smartCardHealth = data?.smartCardHealth;
  const applicationHealth = data?.applicationHealth;
  // Note: API returns auditPackageStat (singular) and vendingMetric (singular)
  const auditPackageStats = data?.auditPackageStat;
  const vendingMetrics = data?.vendingMetric;

  // Fetch metrics history
  const fetchMetricsHistory = useCallback(async () => {
    if (!id) return;

    setMetricsLoading(true);
    setMetricsError(null);

    try {
      const to = Date.now();
      const from = to - timeRange.value;
      const metrics = [
        "heapUsagePercent",
        "processCpuLoad",
        "systemLoadAverage",
        "threadCount",
        "deadlockedThreadCount",
        "smartCardUptimePercent",
        "consecutiveErrorCount",
        "vendingSuccessRate",
        "diskUsagePercent",
        "diskFreeSpaceGB",
      ].join(",");

      const response = await fiscalDeviceService.fetchMetricsHistory(id, from, to, metrics);
      setMetricsHistory(response?.data?.metrics || []);
    } catch (err) {
      console.error("Error fetching metrics history:", err);
      setMetricsError("Greška pri učitavanju istorijskih podataka");
    } finally {
      setMetricsLoading(false);
    }
  }, [id, timeRange]);

  useEffect(() => {
    fetchMetricsHistory();

    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchMetricsHistory, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchMetricsHistory]);

  const renderEmptyState = (message = "Podaci nisu dostupni") => (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>ℹ️</div>
      <p>{message}</p>
      <p className={styles.emptySubtext}>Ova funkcionalnost je dostupna samo za novije verzije softvera.</p>
    </div>
  );

  const renderProgressBar = (value, color = "green") => {
    const numValue = parseFloat(value) || 0;
    return (
      <div className={styles.progressBarContainer}>
        <div
          className={styles.progressBar}
          style={{
            width: `${Math.min(numValue, 100)}%`,
            backgroundColor: color === "red" ? "#dc3545" : color === "orange" ? "#fd7e14" : "#28a745",
          }}
        />
      </div>
    );
  };

  const renderSmartCardHealth = () => {
    if (!smartCardHealth) return renderEmptyState();

    const uptimePercent = parseFloat(smartCardHealth.uptimePercentage) || 0;

    return (
      <div className={styles.cardContent}>
        {/* Critical Alert */}
        {smartCardHealth.physicalInterventionRequired && (
          <div className={styles.alertError}>
            <strong>⚠️ Potrebna fizička intervencija!</strong>
            <br />
            {smartCardHealth.physicalInterventionReason}
          </div>
        )}

        {/* Status Grid */}
        <div className={styles.statsGrid}>
          <div className={styles.statBox}>
            <span className={styles.statLabel}>Status</span>
            <span
              className={styles.statValue}
              style={{ color: smartCardHealth.currentErrorType === "NONE" ? "#28a745" : "#dc3545" }}
            >
              {translateErrorType(smartCardHealth.currentErrorType)}
            </span>
          </div>
          <div className={styles.statBox}>
            <span className={styles.statLabel}>Uptime</span>
            <div className={styles.uptimeContainer}>
              {renderProgressBar(uptimePercent)}
              <span className={styles.uptimeText}>{uptimePercent.toFixed(1)}%</span>
            </div>
          </div>
          <div className={styles.statBox}>
            <span className={styles.statLabel}>Uzastopne greške</span>
            <span
              className={styles.statValue}
              style={{ color: smartCardHealth.consecutiveErrorCount > 5 ? "#dc3545" : "inherit" }}
            >
              {smartCardHealth.consecutiveErrorCount ?? "-"}
            </span>
          </div>
          <div className={styles.statBox}>
            <span className={styles.statLabel}>Ukupno grešaka</span>
            <span className={styles.statValue}>{smartCardHealth.totalErrorCountSinceStartup ?? "-"}</span>
          </div>
        </div>

        <div className={styles.divider} />

        {/* Recovery Stats */}
        <h4 className={styles.sectionSubtitle}>Statistika oporavka</h4>
        <div className={styles.statsGrid}>
          <div className={styles.statBox}>
            <span className={styles.statLabel}>Poslednja akcija</span>
            <span className={styles.statValue}>{translateRecoveryAction(smartCardHealth.lastRecoveryAction)}</span>
          </div>
          <div className={styles.statBox}>
            <span className={styles.statLabel}>Uspešni oporavci</span>
            <span className={styles.statValue}>{smartCardHealth.successfulRecoveryCount ?? "-"}</span>
          </div>
          <div className={styles.statBox}>
            <span className={styles.statLabel}>Neuspešni oporavci</span>
            <span className={styles.statValue}>{smartCardHealth.failedRecoveryCount ?? "-"}</span>
          </div>
          <div className={styles.statBox}>
            <span className={styles.statLabel}>PC/SC restarta</span>
            <span className={styles.statValue}>{smartCardHealth.pcscRestartCount ?? "-"}</span>
          </div>
        </div>

        <p className={styles.footerText}>
          Poslednja uspešna operacija: {formatDateTime(smartCardHealth.lastSuccessfulOperationTimestamp)}
        </p>
      </div>
    );
  };

  const renderApplicationHealth = () => {
    if (!applicationHealth) return renderEmptyState();

    const heapUsage = parseFloat(applicationHealth.heapUsagePercent) || 0;
    const heapColor = heapUsage > 90 ? "red" : heapUsage > 70 ? "orange" : "green";

    return (
      <div className={styles.cardContent}>
        {/* Critical Alerts */}
        {applicationHealth.deadlockedThreadCount > 0 && (
          <div className={styles.alertError}>
            DEADLOCK DETEKTOVAN! ({applicationHealth.deadlockedThreadCount} threadova)
          </div>
        )}
        {applicationHealth.hasStuckTasks && (
          <div className={styles.alertWarning}>
            <div>Zaglavljeni taskovi detektovani:</div>
            {applicationHealth.overdueTasks && applicationHealth.overdueTasks.length > 0 ? (
              <ul className={styles.stuckTasksList}>
                {applicationHealth.overdueTasks.map((task, idx) => (
                  <li key={idx}>{task}</li>
                ))}
              </ul>
            ) : applicationHealth.scheduledTasks?.overdueTasks && applicationHealth.scheduledTasks.overdueTasks.length > 0 ? (
              <ul className={styles.stuckTasksList}>
                {applicationHealth.scheduledTasks.overdueTasks.map((task, idx) => (
                  <li key={idx}>{task}</li>
                ))}
              </ul>
            ) : (
              <span className={styles.alertSubtext}>
                Taskovi su označeni kao zaglavljeni, ali nisu navedeni specifični taskovi.
              </span>
            )}
          </div>
        )}

        {/* Uptime */}
        <div className={styles.statItem}>
          <span className={styles.statLabel}>Uptime</span>
          <span className={styles.statValue}>{formatDuration(applicationHealth.uptimeMs)}</span>
        </div>

        <div className={styles.divider} />

        {/* Memory */}
        <h4 className={styles.sectionSubtitle}>Memorija</h4>
        <div className={styles.memorySection}>
          <div className={styles.memoryHeader}>
            <span>Heap</span>
            <span>
              {applicationHealth.heapUsedMB ?? "-"} / {applicationHealth.heapMaxMB ?? "-"} MB
            </span>
          </div>
          {renderProgressBar(heapUsage, heapColor)}
          <span className={styles.memoryPercent}>{heapUsage.toFixed(1)}% iskorišćeno</span>
        </div>

        {/* CPU & Threads */}
        <div className={styles.statsGrid}>
          <div className={styles.statBox}>
            <span className={styles.statLabel}>CPU Load</span>
            <span className={styles.statValue}>
              {applicationHealth.processCpuLoad != null
                ? `${parseFloat(applicationHealth.processCpuLoad).toFixed(1)}%`
                : "-"}
            </span>
          </div>
          <div className={styles.statBox}>
            <span className={styles.statLabel}>Threadovi</span>
            <span className={styles.statValue}>{applicationHealth.threadCount ?? "-"}</span>
          </div>
        </div>

        <div className={styles.divider} />

        {/* TaxCore Stats */}
        <h4 className={styles.sectionSubtitle}>TaxCore zahtevi</h4>
        <div className={styles.statsGrid}>
          <div className={styles.statBox}>
            <span className={styles.statLabel}>Ukupno</span>
            <span className={styles.statValue}>{applicationHealth.totalTaxCoreRequests ?? "-"}</span>
          </div>
          <div className={styles.statBox}>
            <span className={styles.statLabel}>Neuspešno</span>
            <span
              className={styles.statValue}
              style={{ color: applicationHealth.failedTaxCoreRequests > 0 ? "#dc3545" : "inherit" }}
            >
              {applicationHealth.failedTaxCoreRequests ?? "-"}
            </span>
          </div>
        </div>

        {/* Scheduled Tasks */}
        {applicationHealth.scheduledTasks && (
          <>
            <div className={styles.divider} />
            <h4 className={styles.sectionSubtitle}>Scheduled Taskovi</h4>
            <div className={styles.scheduledTasksTable}>
              {Object.entries(applicationHealth.scheduledTasks)
                .filter(([key, value]) => typeof value === 'object' && value !== null && key !== 'overdueTasks' && key !== 'hasStuckTasks')
                .sort(([, a], [, b]) => {
                  // Sort by last execution time (most recent first)
                  return (b.lastExecutionTimestamp || 0) - (a.lastExecutionTimestamp || 0);
                })
                .map(([taskName, taskData]) => (
                  <div 
                    key={taskName} 
                    className={styles.scheduledTaskRow}
                  >
                    <div className={styles.taskNameCell}>
                      <span className={styles.taskName}>{taskName}</span>
                      {taskData.currentlyRunning && (
                        <span className={styles.runningBadge}>AKTIVAN</span>
                      )}
                    </div>
                    <div className={styles.taskStatsCell}>
                      <span className={styles.taskStat}>
                        ✓ {taskData.successCount || 0}
                      </span>
                      <span className={styles.taskStat} style={{ color: taskData.failureCount > 0 ? '#dc3545' : 'inherit' }}>
                        ✗ {taskData.failureCount || 0}
                      </span>
                      <span className={styles.taskStat}>
                        ⌀ {taskData.avgExecutionTimeMs || 0}ms
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderAuditPackageStats = () => {
    if (!auditPackageStats) return renderEmptyState();

    return (
      <div className={styles.cardContent}>
        {/* Orphan Alert */}
        {auditPackageStats.orphanedCount > 0 && (
          <div className={styles.alertWarning}>
            {auditPackageStats.orphanedCount} orphan paketa detektovano!
            <br />
            <span className={styles.alertSubtext}>Ovi paketi možda neće biti automatski poslati.</span>
          </div>
        )}

        <div className={styles.statsGrid}>
          <div className={styles.statBox}>
            <span className={styles.statLabel}>Fajlova na disku</span>
            <span className={styles.statValue}>{auditPackageStats.filesOnDisk ?? "-"}</span>
          </div>
          <div className={styles.statBox}>
            <span className={styles.statLabel}>U listi za slanje</span>
            <span className={styles.statValue}>{auditPackageStats.inUnsentList ?? "-"}</span>
          </div>
          <div className={styles.statBox}>
            <span className={styles.statLabel}>Orphan paketa</span>
            <span
              className={styles.statValue}
              style={{ color: auditPackageStats.orphanedCount > 0 ? "#fd7e14" : "inherit" }}
            >
              {auditPackageStats.orphanedCount ?? 0}
            </span>
          </div>
        </div>

        {auditPackageStats.lastSubmittedFileName && (
          <p className={styles.footerText}>Poslednji poslat: {auditPackageStats.lastSubmittedFileName}</p>
        )}
      </div>
    );
  };

  const renderVendingMetrics = () => {
    if (!vendingMetrics) return renderEmptyState();

    const successRate = parseFloat(vendingMetrics.successRate) || 0;
    const rateColor = successRate >= 95 ? "green" : successRate >= 80 ? "orange" : "red";

    return (
      <div className={styles.cardContent}>
        {/* Critical Alerts */}
        {vendingMetrics.cardLocked && <div className={styles.alertError}>Kartica je ZAKLJUČANA!</div>}
        {vendingMetrics.wrongPinEntered && (
          <div className={styles.alertError}>Unet pogrešan PIN - automatska inicijalizacija zaustavljena</div>
        )}

        {/* Overall Stats */}
        <div className={styles.statsGrid}>
          <div className={styles.statBox}>
            <span className={styles.statLabel}>Ukupno</span>
            <span className={styles.statValue}>{vendingMetrics.totalVendingInvoices ?? "-"}</span>
          </div>
          <div className={styles.statBox}>
            <span className={styles.statLabel}>Uspešno</span>
            <span className={styles.statValue} style={{ color: "#28a745" }}>
              {vendingMetrics.successfulVendingInvoices ?? "-"}
            </span>
          </div>
          <div className={styles.statBox}>
            <span className={styles.statLabel}>Neuspešno</span>
            <span
              className={styles.statValue}
              style={{ color: vendingMetrics.failedVendingInvoices > 0 ? "#dc3545" : "inherit" }}
            >
              {vendingMetrics.failedVendingInvoices ?? "-"}
            </span>
          </div>
        </div>

        {/* Success Rate Progress */}
        <div className={styles.successRateSection}>
          <div className={styles.successRateHeader}>
            <span>Success Rate</span>
            <span
              className={styles.successRateValue}
              style={{
                color: rateColor === "green" ? "#28a745" : rateColor === "orange" ? "#fd7e14" : "#dc3545",
              }}
            >
              {successRate.toFixed(1)}%
            </span>
          </div>
          {renderProgressBar(successRate, rateColor)}
        </div>

        <div className={styles.divider} />

        {/* Processing Time */}
        <h4 className={styles.sectionSubtitle}>Vreme obrade</h4>
        <div className={styles.statsGrid}>
          <div className={styles.statBox}>
            <span className={styles.statLabel}>Prosečno</span>
            <span className={styles.statValue}>
              {vendingMetrics.averageProcessingTimeMs != null
                ? `${parseFloat(vendingMetrics.averageProcessingTimeMs).toFixed(0)}ms`
                : "-"}
            </span>
          </div>
          <div className={styles.statBox}>
            <span className={styles.statLabel}>Min</span>
            <span className={styles.statValue}>{vendingMetrics.minProcessingTimeMs ?? "-"}ms</span>
          </div>
          <div className={styles.statBox}>
            <span className={styles.statLabel}>Max</span>
            <span className={styles.statValue}>{vendingMetrics.maxProcessingTimeMs ?? "-"}ms</span>
          </div>
        </div>

        {/* Failure Breakdown */}
        {vendingMetrics.failedVendingInvoices > 0 && (
          <>
            <div className={styles.divider} />
            <h4 className={styles.sectionSubtitle}>Razlozi neuspeha</h4>
            
            {/* Last Error */}
            {vendingMetrics.lastErrorMessage && (
              <div className={styles.lastErrorBox}>
                <span className={styles.lastErrorLabel}>Poslednja greška:</span>
                <span className={styles.lastErrorMessage}>{vendingMetrics.lastErrorMessage}</span>
                {vendingMetrics.lastFailedItemIdentifier && (
                  <span className={styles.lastErrorDetail}>
                    Artikal: {vendingMetrics.lastFailedItemIdentifier}
                  </span>
                )}
                {vendingMetrics.lastFailedMacAddress && (
                  <span className={styles.lastErrorDetail}>
                    Kontroler: {vendingMetrics.lastFailedMacAddress}
                  </span>
                )}
              </div>
            )}

            {/* Most Common Failure */}
            {vendingMetrics.mostCommonFailure && vendingMetrics.mostCommonFailure !== "NONE" && (
              <div className={styles.mostCommonFailure}>
                Najčešći problem: <strong>{failureLabels[vendingMetrics.mostCommonFailure.toLowerCase()] || vendingMetrics.mostCommonFailure}</strong>
              </div>
            )}

            {/* Failure Breakdown Table */}
            {vendingMetrics.failureBreakdown && Object.values(vendingMetrics.failureBreakdown).some(v => v > 0) && (
              <div className={styles.failureTable}>
                {Object.entries(vendingMetrics.failureBreakdown)
                  .filter(([_, count]) => count > 0)
                  .sort(([, a], [, b]) => b - a)
                  .map(([key, count]) => (
                    <div key={key} className={styles.failureRow}>
                      <span>{failureLabels[key] || key}</span>
                      <span className={styles.failureCount}>{count}</span>
                    </div>
                  ))}
              </div>
            )}
          </>
        )}

        {/* FSC Sync Stats */}
        <div className={styles.divider} />
        <h4 className={styles.sectionSubtitle}>Sinhronizacija sa FSC</h4>
        <div className={styles.statsGrid}>
          <div className={styles.statBox}>
            <span className={styles.statLabel}>Sačuvano lokalno</span>
            <span className={styles.statValue}>{vendingMetrics.invoicesSavedToLocalDB ?? "-"}</span>
          </div>
          <div className={styles.statBox}>
            <span className={styles.statLabel}>Poslato na FSC</span>
            <span className={styles.statValue}>{vendingMetrics.invoicesSavedToFSC ?? "-"}</span>
          </div>
        </div>

        <p className={styles.footerText}>Poslednja faktura: {formatDateTime(vendingMetrics.lastInvoiceTimestamp)}</p>
      </div>
    );
  };

  // Chart components
  const renderChartLoading = () => (
    <div className={styles.chartLoading}>
      <div className={styles.spinner} />
      <p>Učitavanje...</p>
    </div>
  );

  const renderChartError = () => (
    <div className={styles.chartError}>
      <p>⚠️ {metricsError}</p>
      <button className={styles.retryButton} onClick={fetchMetricsHistory}>
        Pokušaj ponovo
      </button>
    </div>
  );

  const renderChartEmpty = () => (
    <div className={styles.chartEmpty}>
      <p>📊 Nema dostupnih podataka za izabrani period</p>
      <p className={styles.chartEmptySubtext}>Podaci se prikupljaju svakih 5 minuta kada je uređaj online</p>
    </div>
  );

  const renderMemoryChart = () => {
    if (metricsLoading) return renderChartLoading();
    if (metricsError) return renderChartError();
    if (!metricsHistory || metricsHistory.length === 0) return renderChartEmpty();

    return (
      <ResponsiveContainer width="100%" height={250}>
        <AreaChart data={metricsHistory}>
          <defs>
            <linearGradient id="memoryGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#2196F3" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#2196F3" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis dataKey="timestamp" tickFormatter={formatChartTime} stroke="#666" fontSize={12} />
          <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} stroke="#666" fontSize={12} />
          <Tooltip
            labelFormatter={formatChartDateTime}
            formatter={(value) => [`${parseFloat(value)?.toFixed(1)}%`, "Heap"]}
          />
          <ReferenceLine y={90} stroke="#F44336" strokeDasharray="5 5" />
          <ReferenceLine y={70} stroke="#FF9800" strokeDasharray="5 5" />
          <Area
            type="monotone"
            dataKey="heapUsagePercent"
            stroke="#2196F3"
            fill="url(#memoryGradient)"
            strokeWidth={2}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  };

  const renderCpuChart = () => {
    if (metricsLoading) return renderChartLoading();
    if (metricsError) return renderChartError();
    if (!metricsHistory || metricsHistory.length === 0) return renderChartEmpty();

    return (
      <ResponsiveContainer width="100%" height={250}>
        <LineChart data={metricsHistory}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis dataKey="timestamp" tickFormatter={formatChartTime} stroke="#666" fontSize={12} />
          <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} stroke="#666" fontSize={12} />
          <Tooltip
            labelFormatter={formatChartDateTime}
            formatter={(value, name) => [
              `${parseFloat(value)?.toFixed(1)}%`,
              name === "processCpuLoad" ? "Process CPU" : "System Load",
            ]}
          />
          <Legend />
          <Line
            type="monotone"
            dataKey="processCpuLoad"
            name="Process CPU"
            stroke="#4CAF50"
            strokeWidth={2}
            dot={false}
          />
          <Line
            type="monotone"
            dataKey="systemLoadAverage"
            name="System Load"
            stroke="#FF9800"
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const renderVendingSuccessChart = () => {
    if (metricsLoading) return renderChartLoading();
    if (metricsError) return renderChartError();
    if (!metricsHistory || metricsHistory.length === 0) return renderChartEmpty();

    return (
      <ResponsiveContainer width="100%" height={250}>
        <LineChart data={metricsHistory}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis dataKey="timestamp" tickFormatter={formatChartTime} stroke="#666" fontSize={12} />
          <YAxis domain={[90, 100]} tickFormatter={(v) => `${v}%`} stroke="#666" fontSize={12} />
          <Tooltip
            labelFormatter={formatChartDateTime}
            formatter={(value) => [`${parseFloat(value)?.toFixed(2)}%`, "Success Rate"]}
          />
          <ReferenceLine y={95} stroke="#4CAF50" strokeDasharray="5 5" />
          <Line
            type="monotone"
            dataKey="vendingSuccessRate"
            stroke="#9C27B0"
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const renderDiskChart = () => {
    if (metricsLoading) return renderChartLoading();
    if (metricsError) return renderChartError();
    if (!metricsHistory || metricsHistory.length === 0) return renderChartEmpty();

    return (
      <ResponsiveContainer width="100%" height={250}>
        <AreaChart data={metricsHistory}>
          <defs>
            <linearGradient id="diskGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#F44336" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#F44336" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis dataKey="timestamp" tickFormatter={formatChartTime} stroke="#666" fontSize={12} />
          <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} stroke="#666" fontSize={12} />
          <Tooltip
            labelFormatter={formatChartDateTime}
            formatter={(value, name, props) => {
              const point = props?.payload;
              const freeSpace = point?.diskFreeSpaceGB;
              return [
                `${parseFloat(value)?.toFixed(1)}%${freeSpace ? ` (${parseFloat(freeSpace).toFixed(1)} GB slobodno)` : ""}`,
                "Disk",
              ];
            }}
          />
          <ReferenceLine y={90} stroke="#F44336" strokeDasharray="5 5" />
          <Area
            type="monotone"
            dataKey="diskUsagePercent"
            stroke="#F44336"
            fill="url(#diskGradient)"
            strokeWidth={2}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  };

  const renderSmartCardChart = () => {
    if (metricsLoading) return renderChartLoading();
    if (metricsError) return renderChartError();
    if (!metricsHistory || metricsHistory.length === 0) return renderChartEmpty();

    return (
      <ResponsiveContainer width="100%" height={250}>
        <LineChart data={metricsHistory}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis dataKey="timestamp" tickFormatter={formatChartTime} stroke="#666" fontSize={12} />
          <YAxis yAxisId="left" domain={[95, 100]} tickFormatter={(v) => `${v}%`} stroke="#666" fontSize={12} />
          <YAxis yAxisId="right" orientation="right" domain={[0, 10]} stroke="#F44336" fontSize={12} />
          <Tooltip
            labelFormatter={formatChartDateTime}
            formatter={(value, name) => {
              if (name === "smartCardUptimePercent") return [`${parseFloat(value)?.toFixed(2)}%`, "Uptime"];
              if (name === "consecutiveErrorCount") return [value, "Uzastopne greške"];
              return [value, name];
            }}
          />
          <Legend />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="smartCardUptimePercent"
            name="Uptime"
            stroke="#4CAF50"
            strokeWidth={2}
            dot={false}
          />
          <Line
            yAxisId="right"
            type="stepAfter"
            dataKey="consecutiveErrorCount"
            name="Uzastopne greške"
            stroke="#F44336"
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div className={styles.content}>
      {/* Overall Health Status Banner */}
      <div
        className={classNames(
          styles.healthBanner,
          healthStatus.status === "critical"
            ? styles.bannerError
            : healthStatus.status === "warning"
            ? styles.bannerWarning
            : healthStatus.status === "healthy"
            ? styles.bannerSuccess
            : styles.bannerUnknown
        )}
      >
        <div className={styles.bannerIcon}>
          {healthStatus.status === "critical"
            ? "🔴"
            : healthStatus.status === "warning"
            ? "🟡"
            : healthStatus.status === "healthy"
            ? "🟢"
            : "⚪"}
        </div>
        <div className={styles.bannerContent}>
          <h3 className={styles.bannerTitle}>Status zdravlja uređaja</h3>
          <p className={styles.bannerMessage}>{healthStatus.message}</p>
        </div>
      </div>

      {/* Health Cards Grid */}
      <div className={styles.cardsGrid}>
        {/* Smart Card Health */}
        <div className={styles.healthCard}>
          <div className={styles.cardHeader}>
            <h3 className={styles.cardTitle}>Smart Kartica</h3>
            {smartCardHealth && (
              <span
                className={classNames(
                  styles.statusBadge,
                  smartCardHealth.currentErrorType === "NONE" ? styles.badgeSuccess : styles.badgeError
                )}
              >
                {translateErrorType(smartCardHealth.currentErrorType)}
              </span>
            )}
          </div>
          {renderSmartCardHealth()}
        </div>

        {/* Application Health */}
        <div className={styles.healthCard}>
          <div className={styles.cardHeader}>
            <h3 className={styles.cardTitle}>Aplikacija</h3>
          </div>
          {renderApplicationHealth()}
        </div>

        {/* Audit Package Stats */}
        <div className={styles.healthCard}>
          <div className={styles.cardHeader}>
            <h3 className={styles.cardTitle}>Audit Paketi</h3>
          </div>
          {renderAuditPackageStats()}
        </div>

        {/* Vending Metrics */}
        <div className={styles.healthCard}>
          <div className={styles.cardHeader}>
            <h3 className={styles.cardTitle}>Vending Metrike</h3>
            {vendingMetrics && (
              <span
                className={classNames(
                  styles.statusBadge,
                  parseFloat(vendingMetrics.successRate) >= 95
                    ? styles.badgeSuccess
                    : parseFloat(vendingMetrics.successRate) >= 80
                    ? styles.badgeWarning
                    : styles.badgeError
                )}
              >
                {parseFloat(vendingMetrics.successRate)?.toFixed(1) ?? "-"}%
              </span>
            )}
          </div>
          {renderVendingMetrics()}
        </div>
      </div>

      {/* Historical Graphs Section */}
      <div className={styles.graphsSection}>
        <div className={styles.graphsHeader}>
          <h2 className={styles.graphsTitle}>Istorijski podaci</h2>
          <div className={styles.timeRangeSelector}>
            {TIME_RANGES.map((range) => (
              <button
                key={range.label}
                className={classNames(
                  styles.timeRangeButton,
                  timeRange.value === range.value && styles.timeRangeButtonActive
                )}
                onClick={() => setTimeRange(range)}
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.chartsGrid}>
          {/* Memory Chart */}
          <div className={styles.chartCard}>
            <div className={styles.chartHeader}>
              <h3 className={styles.chartTitle}>Memorija (Heap)</h3>
            </div>
            <div className={styles.chartContent}>{renderMemoryChart()}</div>
          </div>

          {/* CPU Chart */}
          <div className={styles.chartCard}>
            <div className={styles.chartHeader}>
              <h3 className={styles.chartTitle}>CPU Opterećenje</h3>
            </div>
            <div className={styles.chartContent}>{renderCpuChart()}</div>
          </div>

          {/* Vending Success Chart */}
          <div className={styles.chartCard}>
            <div className={styles.chartHeader}>
              <h3 className={styles.chartTitle}>Vending Success Rate</h3>
            </div>
            <div className={styles.chartContent}>{renderVendingSuccessChart()}</div>
          </div>

          {/* Disk Chart */}
          <div className={styles.chartCard}>
            <div className={styles.chartHeader}>
              <h3 className={styles.chartTitle}>Disk</h3>
            </div>
            <div className={styles.chartContent}>{renderDiskChart()}</div>
          </div>

          {/* Smart Card Health Chart - Full Width */}
          <div className={classNames(styles.chartCard, styles.chartCardFullWidth)}>
            <div className={styles.chartHeader}>
              <h3 className={styles.chartTitle}>Smart Card Zdravlje</h3>
            </div>
            <div className={styles.chartContent}>{renderSmartCardChart()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FiscalDeviceHealthScreen;
